<script setup lang="ts">
import { useField } from 'vee-validate';
import BaseIconLoader from '@/presentation/components/base/iconLoader/BaseIconLoader.vue';
import type { RuleExpression } from 'vee-validate';
import { normalizeDigits } from '@/presentation/utils/number.util';

type InputValue = string | number | undefined;

interface IProps {
	modelValue: InputValue;
	placeholder?: string;
	type?: string;
	inputClass?: string;
	mainClass?: string;
	isDisable?: boolean;
	preIcon?: string;
	nextIcon?: string;
	errorMessage?: string;
	name: string;
	rules?: RuleExpression<string | number>;
	hint?: string;
	autoFocus?: boolean;
	floatingLabel?: boolean;
	valueFormatter?: (value: InputValue) => string | number;
	valueParser?: (value: string) => InputValue;
	max?: string | number;
}

const props = withDefaults(defineProps<IProps>(), {
	type: 'text',
	autoFocus: false,
	floatingLabel: true,
});
const emit = defineEmits<{
	(e: 'update:modelValue', value: InputValue): void;
	(e: 'changed:errors', value: boolean): void;
	(e: 'click-pre-image'): void;
	(e: 'click-next-image'): void;
}>();

const fieldOptions = {
	initialValue: props.modelValue,
	label: props.placeholder,
	bails: false,
};

// Keep validation rules reactive. Some inputs (for example the service deposit)
// derive their limits from another field that is still empty when this component mounts.
const rules = toRef(props, 'rules');
const { errorMessage, handleChange, validate } = useField(props.name, rules, fieldOptions);

const clickedOnInput = ref(false);
const text = computed({
	get() {
		if (props.valueFormatter) {
			return props.valueFormatter(props.modelValue);
		}
		return props.modelValue ?? '';
	},
	async set(value: string) {
		const normalizedValue = props.type === 'tel' || props.type === 'number' ? normalizeDigits(value) : value;
		let inputValue: InputValue = normalizedValue;
		if (props.valueParser) {
			inputValue = props.valueParser(normalizedValue);
		} else if (props.type === 'number') {
			inputValue = String(inputValue).replace(/[^0-9\-]/g, '');
		}
		emit('update:modelValue', inputValue);
		handleChange(inputValue ?? '', false);
		const validationResult = await validate();
		emit('changed:errors', validationResult.valid);
	},
});

watch(
	() => props.modelValue,
	(value) => {
		handleChange(value ?? '', false);
	},
);
const activeInput = computed(() => !!text.value || clickedOnInput.value);
const input = ref();

const handleInputFocus = () => {
	input?.value?.focus();
};

const handleActiveInput = () => {
	if (!text.value) clickedOnInput.value = !activeInput.value;
	else if (text.value) clickedOnInput.value = true;
};

const captionContent = computed(() => errorMessage.value ?? props.hint);

// TODO: must be update
const captionContentClassModifier = computed(() => {
	if (errorMessage.value) {
		return 'text-red-500';
	}
	if (props.hint) {
		return 'text-zinc-500';
	}
});

onMounted(() => {
	if (props.autoFocus) handleInputFocus();
});
</script>

<template>
	<div class="input" :class="inputClass">
		<div
			class="input-main border bg-white transition duration-200"
			:class="[
				isDisable ? 'bg-gray-200' : 'cursor-pointer hover:border-primary/50 hover:shadow-inputFocusShadow',
				errorMessage?.length ? 'border-error' : activeInput ? 'border-primary shadow-inputFocusShadow' : 'border-slate-200',
				mainClass,
			]"
		>
			<BaseIconLoader v-if="preIcon" :name="preIcon" size="24px" @click="emit('click-pre-image')" />
			<div class="input__box">
				<label
					v-if="floatingLabel"
					:class="{
						'text-red-500': errorMessage?.length,
						'bg-gray-200': isDisable,
						input__box__placeholder: !activeInput,
						'input__box__placeholder--active': activeInput,
					}"
					class="input__box__label text-black-400"
					@click="handleInputFocus"
					>{{ placeholder }}</label
				>
				<input
					v-bind="$attrs"
					ref="input"
					v-model="text"
					:name="name"
					:type="type"
					:max="max"
					:disabled="isDisable"
					:placeholder="floatingLabel ? undefined : placeholder"
					class="text-sm text-black placeholder:text-black-400 focus:ring-0"
					:class="{ 'bg-gray-200 opacity-50': isDisable }"
					@focus="handleActiveInput"
					@blur="handleActiveInput"
				/>
			</div>
			<BaseIconLoader v-if="nextIcon" class="text-black-400" :name="nextIcon" size="24px" @click="emit('click-next-image')" />
		</div>
		<slot name="next-input" />
		<span v-if="captionContent" :class="[captionContentClassModifier, 'absolute pt-[5px] right-[10px] text-xs']">{{ captionContent }}</span>
	</div>
</template>

<style lang="scss" scoped>
.input {
	margin-bottom: 15px;
	position: relative;
	background-color: white;
	border-radius: 16px;

	&-main {
		width: 100%;
		display: flex;
		padding: 12px;
		border-radius: 16px;
		position: relative;
		min-height: 52px;
	}

	input,
	input:active,
	input:focus,
	input:focus-visible {
		width: 100%;
		border: none;
		outline: none;
		padding: 0 8px;
		background: transparent;
	}

	&__box {
		position: relative;
		width: 100%;

		&__label {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			transition: 0.2s all;
			cursor: text;
		}

		&__placeholder {
			position: absolute;
			right: 6px;
			font-size: 16px;
			transition: all 0.2s linear;
		}

		&__placeholder--active {
			padding-inline: 0.5rem;
			max-width: fit-content;
			transition: all 0.2s linear;
			position: absolute;
			font-size: 12px;
			right: 8px;
			background-color: white;
			border-radius: 1rem;
			top: -22px;
		}
	}
}
</style>
